import { useState, useRef, MutableRefObject } from 'react'
import { RequireAtLeastOne } from 'type-fest'

type ItemBase = RequireAtLeastOne<
  {
    [key: string]: any
    id?: string
    uuid?: string
  },
  'id' | 'uuid'
>

type ListNavigationArgs<Item extends ItemBase> = {
  list: Array<Item>
  highlightOnListFocus: boolean
  menuOpen?: boolean
  onSelect: (item: Item) => void
  keydownCallbacks?: {
    arrowDown?: (openMenu: boolean) => void
    arrowUp?: (closeMenu: boolean) => void
    escape?: (e: React.KeyboardEvent) => void
    enter?: () => void
    tab?: () => void
  }
}

type ListNavigationValue = {
  highlightedIndex: number | null
  listRef: MutableRefObject<HTMLUListElement | null>
  onListFocus: () => void
  onListBlur: () => void
  onListKeyDown: (e: React.KeyboardEvent) => void
  resetListHighlight: () => void
  setIsFocusableByKeypress: React.Dispatch<React.SetStateAction<boolean>>
}

function useListKeyboardNavigation<Item extends ItemBase>({
  list,
  highlightOnListFocus = false,
  menuOpen = true,
  onSelect,
  keydownCallbacks,
}: ListNavigationArgs<Item>): ListNavigationValue {
  const [isFocusableByKeypress, setIsFocusableByKeypress] =
    useState(highlightOnListFocus)
  const [highlightedIndex, setHighlightedIndex] = useState<null | number>(null)
  const listRef: MutableRefObject<HTMLUListElement | null> = useRef(null)

  const resetListHighlight = () => setHighlightedIndex(null)

  const handleScroll = (childIndex: number, scrollDirection: 'up' | 'down') => {
    const childElement = listRef.current?.children[childIndex]
    const childElementRect = childElement?.getBoundingClientRect()
    const listElemetRect = listRef.current?.getBoundingClientRect()

    if (!childElement || !childElementRect || !listElemetRect) return

    if (scrollDirection === 'down') {
      const childOffsetFromList = childElementRect.y - listElemetRect.y
      if (
        childOffsetFromList + childElementRect.height >
        listElemetRect.height
      ) {
        childElement.scrollIntoView(false)
      }
    }

    if (childElementRect.y < listElemetRect.y) {
      childElement.scrollIntoView()
    }
  }

  const onListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        e.stopPropagation()
        if (
          menuOpen &&
          list.length &&
          (highlightedIndex === null || highlightedIndex < list.length - 1)
        ) {
          const nextHighlightedIndex =
            highlightedIndex !== null ? highlightedIndex + 1 : 0
          handleScroll(nextHighlightedIndex, 'down')
          setHighlightedIndex(nextHighlightedIndex)
          return keydownCallbacks?.arrowDown?.(false)
        }
        setHighlightedIndex(null)
        keydownCallbacks?.arrowDown?.(true)
        break
      case 'ArrowUp':
        e.preventDefault()
        e.stopPropagation()
        if (menuOpen && highlightedIndex !== null && highlightedIndex > 0) {
          handleScroll(highlightedIndex - 1, 'up')
          setHighlightedIndex(highlightedIndex - 1)
          return keydownCallbacks?.arrowUp?.(false)
        }
        setHighlightedIndex(null)
        keydownCallbacks?.arrowUp?.(true)
        break
      case 'Enter':
      case ' ':
        e.stopPropagation()
        if (highlightedIndex === null) return
        onSelect(list[highlightedIndex])
        keydownCallbacks?.enter?.()
        break
      case 'Tab':
        keydownCallbacks?.tab?.()
        break
      case 'Escape':
        keydownCallbacks?.escape?.(e)
        break
    }
  }

  const onListBlur = () => {
    setIsFocusableByKeypress(true)
    setHighlightedIndex(null)
  }

  const onListFocus = () => {
    if (
      !isFocusableByKeypress ||
      !highlightOnListFocus ||
      highlightedIndex !== null
    )
      return
    handleScroll(0, 'down')
    setHighlightedIndex(0)
  }

  return {
    highlightedIndex,
    listRef,
    onListBlur,
    onListFocus,
    onListKeyDown,
    resetListHighlight,
    setIsFocusableByKeypress,
  }
}

export { useListKeyboardNavigation }
